import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Folder, FolderOpen, ChevronRight, Search, Home } from "lucide-react";
import { folderApi } from "../services/api";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useModal } from "../hooks/useModal";

const MoveToFolderModal = ({
  collectionName,
  currentFolderId,
  excludeFolderId,
  onSelect,
  onClose,
  itemName,
}) => {
  const { translate } = useLanguage();
  const { isInternalProceduresManager, user } = useAuth();
  // Department members (holding a bare department role, not managers) may only
  // move within their own department subtree: no "collection root", and only
  // their bound top-level department folders as tree roots (subtrees inherit
  // and stay writable).
  const restrictToDepartments = collectionName === "internal_procedures" && !isInternalProceduresManager();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [treeFolders, setTreeFolders] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [childrenMap, setChildrenMap] = useState({});
  const [selectedId, setSelectedId] = useState(undefined); // undefined = nothing selected, null = root
  const searchDebounceRef = useRef(null);
  const modalRef = useModal(true, onClose);

  // Load root-level folders
  useEffect(() => {
    folderApi.listFolders(collectionName, null, { limit: 100 }).then((result) => {
      setTreeFolders(result.folders || []);
    }).catch(() => {});
  }, [collectionName]);

  // Search folders
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const result = await folderApi.searchFolders(collectionName, searchQuery.trim());
        setSearchResults(result.folders || []);
      } catch {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchQuery, collectionName]);

  const [loadingFolderIds, setLoadingFolderIds] = useState(new Set());

  const loadChildren = useCallback(async (parentId) => {
    if (childrenMap[parentId]) return;
    setLoadingFolderIds((prev) => new Set([...prev, parentId]));
    try {
      const result = await folderApi.listFolders(collectionName, parentId, { limit: 100 });
      setChildrenMap((prev) => ({ ...prev, [parentId]: result.folders || [] }));
    } catch {
      setChildrenMap((prev) => ({ ...prev, [parentId]: [] }));
    } finally {
      setLoadingFolderIds((prev) => { const next = new Set(prev); next.delete(parentId); return next; });
    }
  }, [collectionName, childrenMap]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        loadChildren(id);
      }
      return next;
    });
  };

  const renderFolder = (folder, depth = 0) => {
    if (folder.id === excludeFolderId) return null;
    const isExpanded = expandedIds.has(folder.id);
    const isSelected = selectedId === folder.id;
    const children = childrenMap[folder.id] || [];

    return (
      <div key={folder.id}>
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors"
          style={{
            paddingLeft: `${12 + depth * 20}px`,
            backgroundColor: isSelected ? "var(--color-mint-muted)" : undefined,
            border: isSelected ? "1px solid var(--color-mint-border)" : "1px solid transparent",
          }}
          onClick={() => setSelectedId(folder.id)}
          onDoubleClick={() => onSelect(folder.id)}
          onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = "var(--color-surface-raised)"; }}
          onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = ""; }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); toggleExpand(folder.id); }}
            className="p-0.5 rounded flex-shrink-0"
            style={{ color: "var(--color-text-muted)" }}
          >
            <ChevronRight
              size={14}
              style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
            />
          </button>
          {isExpanded ? (
            <FolderOpen size={16} style={{ color: "var(--color-mint)" }} className="flex-shrink-0" />
          ) : (
            <Folder size={16} style={{ color: "var(--color-mint)" }} className="flex-shrink-0" />
          )}
          <span className="text-sm truncate" style={{ color: "var(--color-text-primary)" }}>
            {folder.name}
          </span>
        </div>
        {isExpanded && loadingFolderIds.has(folder.id) && (
          <div className="animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={`skel-${folder.id}-${i}`} className="flex items-center gap-2 px-3 py-2" style={{ paddingLeft: `${12 + (depth + 1) * 20}px` }}>
                <div className="w-3.5 h-3.5 flex-shrink-0" />
                <div className="w-4 h-4 rounded bg-[var(--color-skeleton-light)] flex-shrink-0" />
                <div className="h-3.5 w-24 rounded bg-[var(--color-skeleton-light)]" />
              </div>
            ))}
          </div>
        )}
        {isExpanded && !loadingFolderIds.has(folder.id) && children.filter((c) => c.id !== excludeFolderId).map((child) => renderFolder(child, depth + 1))}
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="card rounded-xl border border-[var(--color-border)] p-6 max-w-md w-full mx-4 max-h-[80vh] flex flex-col"
      >
        <h3 className="font-semibold text-base text-[var(--color-text-primary)] mb-1">
          {translate("moveTo")}
        </h3>
        <p className="text-sm text-[var(--color-text-secondary)] mb-4">
          {translate("moveItemDescription", { name: itemName })}
        </p>

        {/* Search */}
        <div className="relative mb-3">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--color-text-muted)" }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={translate("searchFolders")}
            className="field-input w-full pl-8 text-sm"
          />
        </div>

        {/* Folder tree or search results */}
        <div className="flex-1 overflow-y-auto min-h-[200px] space-y-0.5">
          {searchResults !== null ? (
            searchResults.length === 0 ? (
              <p className="text-sm text-center py-6" style={{ color: "var(--color-text-muted)" }}>
                {translate("noFoldersFound")}
              </p>
            ) : (
              searchResults.filter((f) => f.id !== excludeFolderId).map((folder) => (
                <div
                  key={folder.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors"
                  style={{
                    backgroundColor: selectedId === folder.id ? "var(--color-mint-muted)" : undefined,
                    border: selectedId === folder.id ? "1px solid var(--color-mint-border)" : "1px solid transparent",
                  }}
                  onClick={() => setSelectedId(folder.id)}
                  onDoubleClick={() => onSelect(folder.id)}
                  onMouseEnter={(e) => { if (selectedId !== folder.id) e.currentTarget.style.backgroundColor = "var(--color-surface-raised)"; }}
                  onMouseLeave={(e) => { if (selectedId !== folder.id) e.currentTarget.style.backgroundColor = ""; }}
                >
                  <Folder size={16} style={{ color: "var(--color-mint)" }} className="flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm block truncate" style={{ color: "var(--color-text-primary)" }}>
                      {folder.name}
                    </span>
                    {folder.breadcrumb_path && (
                      <span className="text-xs block truncate" style={{ color: "var(--color-text-muted)" }}>
                        {folder.breadcrumb_path}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )
          ) : (
            <>
              {/* Collection root option */}
              {!restrictToDepartments && (
                <div
                  className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors"
                  style={{
                    backgroundColor: selectedId === null ? "var(--color-mint-muted)" : undefined,
                    border: selectedId === null ? "1px solid var(--color-mint-border)" : "1px solid transparent",
                  }}
                  onClick={() => setSelectedId(null)}
                  onDoubleClick={() => onSelect(null)}
                  onMouseEnter={(e) => { if (selectedId !== null) e.currentTarget.style.backgroundColor = "var(--color-surface-raised)"; }}
                  onMouseLeave={(e) => { if (selectedId !== null) e.currentTarget.style.backgroundColor = ""; }}
                >
                  <Home size={16} style={{ color: "var(--color-text-muted)" }} className="flex-shrink-0" />
                  <span className="text-sm" style={{ color: "var(--color-text-primary)" }}>
                    {translate("collectionRoot")}
                  </span>
                </div>
              )}
              {(restrictToDepartments
                ? treeFolders.filter((f) => f.required_role && (user?.roles || []).includes(f.required_role))
                : treeFolders
              ).map((folder) => renderFolder(folder))}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 mt-4 pt-4" style={{ borderTop: "1px solid var(--color-border)" }}>
          <button onClick={onClose} className="btn-secondary">
            {translate("cancel")}
          </button>
          <button
            onClick={() => {
              if (selectedId !== undefined) onSelect(selectedId);
            }}
            disabled={selectedId === undefined}
            className="btn-primary disabled:opacity-50"
          >
            {translate("moveHere")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MoveToFolderModal;
